import { useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useServiceRef } from './useServiceRef';
import { Cart, CartService } from '@services/cart';
import { CartContext } from '@utils/CartContext';
import { ProductService } from '@services/product';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { restoreFromTenantStorage } from '@utils/googleShoppingAgentStorage';
import { getCartDraft } from '@utils/getCartDraft';
import { setShoppingEventCredentials } from '@utils/analyticsEvents';
import { googleShoppingAgentConfigs } from '@constants/googleShoppingAgentConfigs';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';

export const useGoogleShoppingAgent = (): void => {
  const cartServiceRef = useServiceRef(CartService);
  const productServiceRef = useServiceRef(ProductService);
  const { setCart } = useContext(CartContext);
  const router = useRouter();

  useEffect(() => {
    const currentTenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.key ?? null;
    if (!isFeatureEnabled(FEATURES.GOOGLE_SHOPPING_AGENT)) {
      return;
    }
    if (currentTenantKey) {
      restoreFromTenantStorage(currentTenantKey);
    }
    const raw = process.env.NEXT_PUBLIC_GOOGLE_SHOPPING_AGENT_IDS || '{}';
    const agentIdsMapping: Record<string, string> =
      typeof raw === 'string' ? JSON.parse(raw) : raw;
    const agentId = agentIdsMapping[currentTenantKey];
    if (!agentId) return;

    const cartUrl = new URL(window.location.href);
    cartUrl.searchParams.set('is-cart-drawer-open', 'true');

    window.googleAgentSettings = {
      ...(googleShoppingAgentConfigs[agentId] || {}),
      id: agentId,
      pageUrl: window.location.href,
      settings: {
        'cart-url': cartUrl.toString(),
        ...(googleShoppingAgentConfigs[agentId]?.settings || {}),
      },
      tokenFunc: async (): Promise<{ sessionId: string; token: string }> => {
        const accessToken = Cookies.get('ct_access_token');
        const storedSessionId = JSON.parse(
          localStorage.getItem('GSA:sessionId') || '""',
        );
        const storedSessionIdExpiresAt = JSON.parse(
          localStorage.getItem('GSA:sessionIdExpiresAt') || 'null',
        );
        const isSessionIdValid =
          storedSessionIdExpiresAt &&
          Number(storedSessionIdExpiresAt) > Date.now();

        const url = new URL(`${process.env.NEXT_PUBLIC_API_BASE_URL}/agentic/token`);
        if (storedSessionId && isSessionIdValid) {
          url.searchParams.set('sessionId', storedSessionId);
        }
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken || ''}`,
            'volcano-tenant-key': currentTenantKey,
          },
        });

        if (!response.ok) {
          // eslint-disable-next-line no-console
          console.error(`Failed to fetch token: ${response.statusText}`);
        }
        const { sessionId, token } = await response.json();

        setShoppingEventCredentials({ token, sessionId });

        return { sessionId, token };
      },
      cartVersion: 2,
      handleGetCart: async (): Promise<AddToCartEventDetail[]> => {
        const cart =
          await cartServiceRef.current.getMyActiveCart(currentTenantKey);
        if (!cart?.lineItems?.length) {
          return [];
        }

        return cart.lineItems.map(({ productId, quantity }) => ({
          productId,
          quantity,
        }));
      },
      handleCartUpdate: async (payload, action): Promise<CartUpdateResult> => {
        if (!payload?.length) return { success: true };

        let cart =
          await cartServiceRef.current.getMyActiveCart(currentTenantKey);

        const errors: string[] = [];

        for (const { productId, quantity } of payload) {
          try {
            const productInCart = cart?.lineItems?.find(
              item => item.productId === productId,
            );
            const productDetails =
              await productServiceRef.current.getProductData(productId);

            if (!productDetails) {
              errors.push(`Product not found: ${productId}`);
              continue;
            }

            const variantId = productDetails?.variants?.[0]?.id;
            if (!variantId) {
              errors.push(`Product variant not found: ${productId}`);
              continue;
            }

            const lineItemDraft = { productId, variantId, quantity };
            let updatedCart: Cart | null = null;

            if (!cart) {
              const cartDraft = getCartDraft(
                lineItemDraft,
                productDetails?.price?.currencyCode,
              );
              updatedCart = await cartServiceRef.current.createCart(cartDraft);
            } else if (productInCart) {
              const newQuantity =
                action === 'set' ? quantity : productInCart.quantity + quantity;
              updatedCart = await cartServiceRef.current.changeLineItemQuantity(
                cart.id,
                cart.version,
                productInCart.id,
                newQuantity,
              );
            } else {
              updatedCart = await cartServiceRef.current.addLineItem(
                cart.id,
                cart.version,
                lineItemDraft,
              );
            }

            if (!updatedCart) {
              errors.push(`Failed to update cart: ${cart?.id}`);
              continue;
            }

            cart = updatedCart;
            setCart(cart);
          } catch (error) {
            errors.push(
              error instanceof Error
                ? error.message
                : `Unknown error: ${error}`,
            );
          }
        }

        if (errors.length) return { success: false, error: errors.join(', ') };

        return { success: true };
      },
    };
  }, []);

  useEffect((): void => {
    if (window.googleAgentSettings) {
      window.googleAgentSettings.pageUrl = window.location.href;
    }
  }, [ router.asPath ]);
};
